import { useCallback, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import axios from 'axios';
import { getBearerGrantTypeAuthHeaders } from 'helpers/auth_helpers';
import { getAccessorialRequests } from 'utils/AccessorialRequestWebAPIUtils';
async function fetchOrderLegSummaries(orderId) {
    var _a;
    let response;
    try {
        response = await axios({
            method: 'GET',
            url: `${API_HOST}/api/v3/jobs/orders/${orderId}/legs`,
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: getBearerGrantTypeAuthHeaders(),
            },
        });
    }
    catch (err) {
        const status = axios.isAxiosError(err) ? (_a = err.response) === null || _a === void 0 ? void 0 : _a.status : undefined;
        throw new Error(`Failed to fetch legs for order ${orderId}${status ? ` (HTTP ${status})` : ''}`);
    }
    const body = response.data;
    const legs = Array.isArray(body) ? body : body.data;
    return (legs || []).map((leg) => {
        var _a;
        return ({
            _id: leg._id,
            shipment_id: (_a = leg.shipment_id) !== null && _a !== void 0 ? _a : '',
        });
    });
}
export function useOrderAccessorialRequests(orderId) {
    var _a, _b;
    const { data: legs = [], isLoading: isLoadingLegs, error: legsError, } = useQuery({
        queryKey: ['orderLegs', orderId],
        queryFn: () => fetchOrderLegSummaries(orderId),
        enabled: Boolean(orderId),
    });
    const queries = useQueries({
        queries: legs.map((leg) => ({
            queryKey: ['accessorialRequests', leg._id],
            queryFn: async () => {
                var _a, _b;
                try {
                    const response = await getAccessorialRequests({ shipment: leg._id });
                    return ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.request) !== null && _b !== void 0 ? _b : []);
                }
                catch (err) {
                    console.error(err);
                    throw new Error(`Failed to fetch accessorial requests for shipment ${leg._id}`);
                }
            },
            enabled: Boolean(leg._id),
        })),
    });
    const isLoadingRequests = queries.some((q) => q.isLoading);
    const accessorialError = (_b = (_a = queries.find((q) => q.error)) === null || _a === void 0 ? void 0 : _a.error) !== null && _b !== void 0 ? _b : null;
    const allRequests = useMemo(() => {
        var _a;
        const requests = [];
        for (const query of queries) {
            for (const req of (_a = query.data) !== null && _a !== void 0 ? _a : []) {
                requests.push(req);
            }
        }
        return requests;
    }, [queries]);
    const refetch = useCallback(() => {
        for (const query of queries) {
            query.refetch();
        }
    }, [queries]);
    return {
        allRequests,
        isLoading: isLoadingLegs || (legs.length > 0 && isLoadingRequests),
        error: legsError !== null && legsError !== void 0 ? legsError : accessorialError,
        legs,
        refetch,
    };
}
